import {
  ProductViewed,
  ProductClicked,
  EventBeamModel,
  ElementClicked,
  FieldInputted,
  ProductListViewed
} from '@/types/tracking'
import debounce from 'lodash/debounce'
import generics from '@/utils/generics'
import { StoreModel } from '@/store'
import { Action, Thunk, action, thunk } from 'easy-peasy'

const eventTypes = {
  ProductViewed: 'redventures.ecommerce.v1.ProductViewed',
  ProductClicked: 'redventures.ecommerce.v1.ProductClicked',
  FieldInputted: 'redventures.usertracking.v3.FieldInputted',
  ElementViewed: 'redventures.usertracking.v3.ElementViewed',
  ElementClicked: 'redventures.usertracking.v3.ElementClicked',
  ProductListViewed: 'redventures.ecommerce.v1.ProductListViewed',
  ProductListFiltered: 'redventures.ecommerce.v1.ProductListFiltered'
}

export interface TrackingModel {
  // State
  trackedEvents: EventBeamModel[]
  correlationId: string

  // Actions
  resetEvents: Action<TrackingModel>
  setCorrelationId: Action<TrackingModel, string>
  recordEvent: Action<TrackingModel, EventBeamModel>

  // Thunks
  fireBeam: Thunk<TrackingModel, Partial<EventBeamModel>>

  onInputTracking: Thunk<TrackingModel, FieldInputted, unknown, StoreModel>
  onProductClickedTracking: Thunk<
    TrackingModel,
    ProductClicked,
    unknown,
    StoreModel
  >
  onElementClickedTracking: Thunk<
    TrackingModel,
    ElementClicked,
    unknown,
    StoreModel
  >
  onProductListViewedTracking: Thunk<
    TrackingModel,
    ProductListViewed,
    unknown,
    StoreModel
  >
  onProductViewedTracking: Thunk<
    TrackingModel,
    ProductViewed,
    unknown,
    StoreModel
  >
}

const tracking: TrackingModel = {
  // State
  trackedEvents: [],
  /**
   * Cohesion already exposes a unique uuid that changes on page load. Lets use
   * that as the initial correlation id
   */
  correlationId: globalThis._Cohesion?.instanceId,

  // Actions
  recordEvent: action((state, event) => {
    state.trackedEvents.push(event)
  }),

  resetEvents: action(state => {
    state.trackedEvents = []
  }),

  setCorrelationId: action((state, id) => {
    state.correlationId = id
  }),

  // Thunks
  fireBeam: thunk((actions, { eventName, eventPayload, skipLowerCase }) => {
    const payload = {
      '@type': eventTypes[eventName],
      ...generics.lowerCaseValues(eventPayload, skipLowerCase)
    }

    if (typeof globalThis.tagular === 'function') {
      globalThis.tagular('beam', eventName, payload)
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('Event fired', eventName, eventPayload)
    }

    actions.recordEvent({ eventName, eventPayload: payload })
  }),

  onElementClickedTracking: thunk((actions, payload, { getState }) => {
    payload = { ...payload, correlationId: getState().correlationId }

    actions.fireBeam({
      eventName: 'ElementClicked',
      eventPayload: payload,
      skipLowerCase: ['name', 'actionOutcome']
    })
  }),

  onProductClickedTracking: thunk((actions, payload, { getState }) => {
    payload = { ...payload, correlationId: getState().correlationId }

    actions.fireBeam({
      eventName: 'ProductClicked',
      eventPayload: payload,
      skipLowerCase: ['name', 'actionOutcome']
    })
  }),

  onInputTracking: thunk((actions, payload, { getState }) => {
    payload = { ...payload, correlationId: getState().correlationId }

    debounce(() => {
      actions.fireBeam({
        eventName: 'FieldInputted',
        eventPayload: payload,
        skipLowerCase: ['fieldType']
      })
    }, 2000)()
  }),

  onProductListViewedTracking: thunk(
    (actions, payload, { getStoreState, getState }) => {
      const queryResults = getStoreState().search.results
      const listId = getStoreState().search.listId

      const trackedEvents = getState().trackedEvents

      const existingEvent = trackedEvents.find(event => {
        if (event.eventName === 'ProductListViewed') {
          const ep = event.eventPayload as ProductListViewed
          return ep.listId === listId
        }
        return false
      })

      if (existingEvent) {
        // event has already been tracked
        return
      }

      payload = {
        ...payload,
        listId,
        category: getStoreState().search.dimensions.buy_sell,
        field: [
          {
            ...payload.field[0],
            fieldValue: getStoreState().search.dimensions.zipcode
          },
          {
            ...payload.field[1],
            fieldValue: getStoreState().search.dimensions.buy_sell
          },
          {
            ...payload.field[2],
            fieldValue: getStoreState().search.dimensions.price_from.toString()
          },
          {
            ...payload.field[3],
            fieldValue: getStoreState().search.dimensions.price_to.toString()
          },
          {
            ...payload.field[4],
            fieldValue: getStoreState().search.dimensions.property_type
          },
          {
            ...payload.field[5],
            fieldValue: getStoreState().search.dimensions.sort_by
          }
        ],
        reasonNoCoverage:
          queryResults.length === 0 ? getStoreState().search.h1Tag : '',
        paidListings: getStoreState().search.results.length,
        standardListings: getStoreState().search.results.length
      } as ProductListViewed

      actions.fireBeam({
        eventName: 'ProductListViewed',
        eventPayload: payload,
        skipLowerCase: ['location', 'fieldType']
      })
    }
  ),

  onProductViewedTracking: thunk((actions, payload, { getState }) => {
    const trackedEvents = getState().trackedEvents

    const existingEvent = trackedEvents.find(event => {
      if (event.eventName === 'ProductViewed') {
        const ep = event.eventPayload as ProductViewed

        return (
          ep.product.productId === payload.product.productId &&
          ep.listId === payload.listId
        )
      }
      return false
    })

    if (existingEvent) {
      // event has already been tracked
      return
    }

    actions.fireBeam({ eventName: 'ProductViewed', eventPayload: payload })
  })
}

export default tracking
